/**
 * Клиент файлов (E6). Файлы ходят СТРИМОМ через api (Р-8): загрузка — сырым телом запроса
 * (имя в заголовке `X-Filename`, тип в `Content-Type`), скачивание — обычной ссылкой (сессия —
 * cookie HttpOnly, Р-5). Типы согласованы с сервером по значениям (apps/api/src/files/store.ts).
 *
 * Права — подсказка UI (сервер авторитетен, матрица 4.3, правило 5): загрузка/замена —
 * `contract:register` (делопроизводитель/юрист/админ); скачивание — всем ролям с просмотром.
 */

import type { ApiResult } from "./contracts";

/** Версия файла (метаданные одной загрузки/замены). */
export interface FileVersionView {
  readonly id: string;
  readonly version: number;
  readonly originalName: string;
  readonly contentType: string;
  readonly size: number;
  readonly uploadedById: string | null;
  readonly createdAt: string;
}

/** Логический файл: текущая версия + история (новые сверху). */
export interface FileItem {
  readonly id: string;
  readonly contractId: string | null;
  readonly specificationId: string | null;
  readonly current: FileVersionView;
  readonly versions: FileVersionView[];
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Владелец файла — договор в целом или конкретная спецификация (ТЗ 4.2.3). */
export type FileOwner =
  | { readonly kind: "contract"; readonly id: string }
  | { readonly kind: "specification"; readonly id: string };

/** Лимит и allowlist — зеркало сервера (fileTypes.ts). UI-проверка ускоряет отказ, но не заменяет серверную. */
export const MAX_FILE_SIZE = 50 * 1024 * 1024;

/** Разрешённые расширения для атрибута accept и подсказок. */
export const ALLOWED_ACCEPT = ".pdf,.jpg,.jpeg,.png,.docx";

/**
 * Типы файлов словами — для журнала аудита (D-51, дополнение 2 владельца 13.09.2026). Ключи —
 * канонические MIME, которыми сервер помечает версию файла (`CANONICAL_MIME` в
 * apps/api/src/files/fileTypes.ts): в журнал попадают именно они. Слова — те же четыре типа, что
 * в подсказке «Разрешены только PDF, JPG, PNG, DOCX». Новый тип на сервере без строки здесь
 * роняет сторож audit-entities.test.ts.
 */
export const FILE_TYPE_LABELS: Record<string, string> = {
  "application/pdf": "документ PDF",
  "image/jpeg": "изображение JPG",
  "image/png": "изображение PNG",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "документ Word (DOCX)",
};

const ALLOWED_EXTENSIONS = new Set(["pdf", "jpg", "jpeg", "png", "docx"]);

/** Клиентская проверка перед отправкой: расширение из allowlist и размер ≤ 50 МБ. */
export function validateFile(file: {
  name: string;
  size: number;
}): { ok: true } | { ok: false; message: string } {
  const dot = file.name.lastIndexOf(".");
  const ext = dot >= 0 ? file.name.slice(dot + 1).toLowerCase() : "";
  if (!ALLOWED_EXTENSIONS.has(ext)) {
    return { ok: false, message: "Разрешены только PDF, JPG, PNG, DOCX." };
  }
  if (file.size > MAX_FILE_SIZE) {
    return { ok: false, message: "Файл больше 50 МБ. Уменьшите размер и повторите." };
  }
  if (file.size === 0) {
    return { ok: false, message: "Файл пустой." };
  }
  return { ok: true };
}

/** Размер в байтах → человекочитаемо (Б/КБ/МБ), русский разделитель. */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0).replace(".", ",")} КБ`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb < 10 ? 1 : 0).replace(".", ",")} МБ`;
}

function ownerPath(owner: FileOwner): string {
  return owner.kind === "contract"
    ? `/api/files/contract/${encodeURIComponent(owner.id)}`
    : `/api/files/specification/${encodeURIComponent(owner.id)}`;
}

/** Ссылка скачивания текущей версии (используется в `<a href>` — браузер шлёт cookie сам). */
export function fileDownloadUrl(fileId: string): string {
  return `/api/files/${encodeURIComponent(fileId)}/download`;
}

/** Ссылка скачивания конкретной версии из истории. */
export function versionDownloadUrl(fileId: string, versionId: string): string {
  return `/api/files/${encodeURIComponent(fileId)}/versions/${encodeURIComponent(versionId)}/download`;
}

async function toFailure(res: Response): Promise<Extract<ApiResult<never>, { ok: false }>> {
  if (res.status === 401) {
    return { ok: false, reason: "unauthorized", message: "Сессия истекла. Войдите заново." };
  }
  const body = (await res.json().catch(() => null)) as { message?: string } | null;
  const message = body?.message;
  if (res.status === 403)
    return { ok: false, reason: "forbidden", message: message ?? "Недостаточно прав." };
  if (res.status === 404)
    return { ok: false, reason: "network", message: message ?? "Не найдено." };
  if (res.status === 413) {
    return { ok: false, reason: "validation", message: message ?? "Файл больше 50 МБ." };
  }
  if (res.status === 400) {
    return { ok: false, reason: "validation", message: message ?? "Файл не принят." };
  }
  return { ok: false, reason: "network", message: message ?? "Не удалось выполнить операцию." };
}

/** Список файлов владельца (GET /api/files/{contract|specification}/:id). */
export async function fetchFiles(owner: FileOwner): Promise<ApiResult<FileItem[]>> {
  let res: Response;
  try {
    res = await fetch(ownerPath(owner), {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { files?: FileItem[] } | null;
  return { ok: true, value: body?.files ?? [] };
}

/** Общая отправка файла сырым телом (загрузка/замена). */
async function sendFile(url: string, file: File): Promise<ApiResult<FileItem>> {
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: {
        // Тип берём от файла; пустой — сервер определит по расширению и «магии».
        "Content-Type": file.type || "application/octet-stream",
        "X-Filename": encodeURIComponent(file.name),
        Accept: "application/json",
      },
      credentials: "same-origin",
      body: file,
    });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body = (await res.json().catch(() => null)) as { file?: FileItem } | null;
  if (!body?.file) return { ok: false, reason: "network", message: "Неожиданный ответ сервера." };
  return { ok: true, value: body.file };
}

/** Загрузка нового файла к договору/спецификации (POST /api/files/{owner}/:id). */
export function uploadFile(owner: FileOwner, file: File): Promise<ApiResult<FileItem>> {
  return sendFile(ownerPath(owner), file);
}

/** Замена файла новой версией (POST /api/files/:fileId/versions). */
export function replaceFile(fileId: string, file: File): Promise<ApiResult<FileItem>> {
  return sendFile(`/api/files/${encodeURIComponent(fileId)}/versions`, file);
}
